import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { ClienteFormModal } from '@/components/ClienteFormModal';

type Cliente = {
  id: string;
  codigo_cliente: string;
  nome: string;
  documento: string | null;
  email: string | null;
  telefone: string | null;
  endereco: string | null;
  cidade: string | null;
  estado: string | null;
  cep: string | null;
  apelido: string | null;
  inscricao_estadual: string | null;
  inscricao_municipal: string | null;
  email_nfe: string | null;
  fora_municipio: boolean | null;
  ibge_municipio: string | null;
  limite_credito: number | null;
  observacoes: string | null;
  cnpj_bancario: string | null;
  banco: string | null;
  pix: string | null;
  conta: string | null;
  agencia: string | null;
  matricula_cei_cno: string | null;
  tipo_cliente_id: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  tipo_clientes?: { nome: string } | null;
};

export default function ClienteEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);

  const canWrite = hasPermission('clientes', 'clientes', 'write');

  useEffect(() => {
    if (!canWrite) {
      setLoading(false);
      return;
    }

    loadCliente();
  }, [id, canWrite]);

  useEffect(() => {
    if (cliente && !loading) {
      setShowEditModal(true);
    }
  }, [cliente, loading]);

  const loadCliente = async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('clientes')
        .select(`
          *,
          tipo_clientes (
            nome
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setCliente({ 
        ...data, 
        ativo: data.status === 'ativo',
        tipo_clientes: data.tipo_clientes && !('error' in data.tipo_clientes) ? data.tipo_clientes : null
      });
    } catch (error: any) {
      console.error('Erro ao carregar cliente:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar cliente: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSuccess = () => {
    toast({
      title: "Sucesso",
      description: "Cliente atualizado com sucesso!",
    });
    navigate(`/intranet/clientes/${id}`);
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    navigate('/intranet/clientes-new');
  };

  if (!canWrite) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Você não tem permissão para editar clientes.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!cliente) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Cliente não encontrado.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/intranet/clientes-new')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Editar Cliente</h1>
          <p className="text-muted-foreground">
            {cliente.nome} - Código: {cliente.codigo_cliente}
          </p>
        </div>
      </div>

      <ClienteFormModal
        open={showEditModal}
        onOpenChange={handleModalClose}
        onSuccess={handleEditSuccess}
        clienteToEdit={cliente}
      />
    </div>
  );
}